import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { type Envelope, unwrapFanoutArray, firstVendor } from './_shared';

// ============================================
// WAF rules + events  (/cdn/domains/:d/waf/*)
// ============================================
export interface WAFRule {
  id: number;
  rule_id: string;
  expression: string;
  action: string;
  block_status: number;
  priority: number;
  enabled: boolean;
}

export function useWafRules(domain: string | undefined) {
  return useQuery({
    queryKey: ['cdn', 'domains', domain ?? '', 'waf', 'rules'],
    enabled: !!domain,
    retry: false,
    queryFn: async (): Promise<WAFRule[]> => {
      try {
        const res = await serviceApiV3.get<Envelope<unknown>>(`/cdn/domains/${domain}/waf/rules`);
        return unwrapFanoutArray<WAFRule>(res.data?.data);
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404) return [];
        throw e;
      }
    },
  });
}

export function useSaveWafRule(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, { id?: number } & Omit<WAFRule, 'id'>>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...rule }) => {
      const res =
        id && id > 0
          ? await serviceApiV3.put(`/cdn/domains/${domain}/waf/rules/${id}`, rule)
          : await serviceApiV3.post(`/cdn/domains/${domain}/waf/rules`, rule);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ['cdn', 'domains', domain, 'waf', 'rules'] });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteWafRule(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, number>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await serviceApiV3.delete(`/cdn/domains/${domain}/waf/rules/${id}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ['cdn', 'domains', domain, 'waf', 'rules'] });
      options?.onSuccess?.(...args);
    },
  });
}

export function useWafEvents(domain: string | undefined, query: string) {
  return useQuery({
    queryKey: ['cdn', 'domains', domain ?? '', 'waf', 'events', query],
    enabled: !!domain,
    retry: false,
    queryFn: async (): Promise<unknown> => {
      try {
        const res = await serviceApiV3.get<Envelope<unknown>>(`/cdn/domains/${domain}/waf/events?${query}`);
        // BE fans out: `{ data: { "<provider>": { events, total } } }`.
        return firstVendor<unknown>(res.data?.data, null);
      } catch {
        return null;
      }
    },
  });
}

export function useWafStats(domain: string | undefined, query: string) {
  return useQuery({
    queryKey: ['cdn', 'domains', domain ?? '', 'waf', 'stats', query],
    enabled: !!domain,
    retry: false,
    queryFn: async (): Promise<Record<string, unknown> | null> => {
      try {
        const res = await serviceApiV3.get<Envelope<unknown>>(`/cdn/domains/${domain}/waf/stats?${query}`);
        // BE fans out: `{ data: { "<provider>": { overview, timeline, top_* } } }`.
        return firstVendor<Record<string, unknown> | null>(res.data?.data, null);
      } catch {
        return null;
      }
    },
  });
}

// ============================================
// AI Ruleset catalog + expression test  (global, domain-anchored)
// ============================================
export interface WafAiCatalogRule {
  id: string;
  rule_id: string;
  name: string;
  category: string;
  severity: string;
  pattern?: string;
  action?: string;
  anomaly_score?: number;
}

/** The shared AI ruleset catalog (~2,280 ML-scored rules). */
export function useWafAiCatalog(domain: string | undefined) {
  return useQuery({
    queryKey: ['cdn', 'waf', 'ai-catalog', domain ?? ''],
    enabled: !!domain,
    retry: false,
    queryFn: async (): Promise<WafAiCatalogRule[]> => {
      try {
        const res = await serviceApiV3.get<Envelope<unknown>>(`/cdn/waf/ai-catalog?domain_id=${domain}`);
        // BE fans out: `{ data: { "<provider>": { count, rules: [...] } } }` — the
        // catalog lives under the vendor's `rules` (not the vendor value itself).
        const vendor = firstVendor<unknown>(res.data?.data, null);
        const rules = Array.isArray(vendor)
          ? vendor
          : vendor && typeof vendor === 'object' && Array.isArray((vendor as { rules?: unknown }).rules)
            ? (vendor as { rules: unknown[] }).rules
            : [];
        // The catalog identifies each rule only by `id` (uuid) — alias it to
        // `rule_id` so the disabled/monitor arrays key off a stable value.
        return (rules as Record<string, unknown>[]).map((r) => ({
          id: String(r.id ?? r.rule_id ?? ''),
          rule_id: String(r.rule_id ?? r.id ?? ''),
          name: String(r.name ?? ''),
          category: String(r.category ?? 'other'),
          severity: String(r.severity ?? 'low'),
          pattern: r.pattern as string | undefined,
          action: r.action as string | undefined,
          anomaly_score: r.anomaly_score as number | undefined,
        }));
      } catch {
        return [];
      }
    },
  });
}

export interface WafExprResult {
  valid: boolean;
  error?: string;
}

/** Validate a custom-rule wirefilter expression before saving. */
export function useTestWafExpression(
  domain: string,
  options?: UseMutationOptions<WafExprResult, unknown, string>,
) {
  return useMutation({
    mutationFn: async (expression: string): Promise<WafExprResult> => {
      const res = await serviceApiV3.post<Envelope<unknown>>(`/cdn/waf/test?domain_id=${domain}`, { expression });
      return firstVendor<WafExprResult>(res.data?.data, { valid: false, error: 'No response from validator.' });
    },
    ...options,
  });
}

// ============================================
// Firewall rules  (/cdn/domains/:d/firewall-rules/*)
// ============================================
export interface FwCondition {
  condition_group: number;
  field: string;
  field_key: string | null;
  operator: string;
  value: string;
  negate: boolean;
  transformations: string[];
  case_insensitive: boolean;
  sequence: number;
}
export interface FirewallRule {
  // BE rule id is a UUID string (CDNFirewallRule.ID `json:"id"`). Treating it as
  // a number broke the PUT-vs-POST check below and duplicated rules on save.
  id: string;
  name: string;
  description: string;
  priority: number;
  enabled: boolean;
  action: string;
  action_params: Record<string, unknown>;
  tags: string[];
  conditions: FwCondition[];
  hits?: number;
  last_hit?: string | null;
}
export interface FirewallList {
  enabled: boolean;
  rules: FirewallRule[];
}
export function useFirewallRules(domain: string | undefined) {
  return useQuery({
    queryKey: ['cdn', 'domains', domain ?? '', 'firewall-rules'],
    enabled: !!domain,
    retry: false,
    queryFn: async (): Promise<FirewallList> => {
      try {
        const [rulesRes, statusRes] = await Promise.all([
          serviceApiV3.get<Envelope<unknown>>(`/cdn/domains/${domain}/firewall-rules`),
          serviceApiV3.get<Envelope<unknown>>(`/cdn/domains/${domain}/firewall-rules/status`),
        ]);
        const rules = firstVendor<FirewallRule[]>(rulesRes.data?.data, []);
        const status = firstVendor<{ enabled?: boolean }>(statusRes.data?.data, { enabled: false });
        return { enabled: status.enabled ?? false, rules: Array.isArray(rules) ? rules : [] };
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404) return { enabled: false, rules: [] };
        throw e;
      }
    },
  });
}

type FirewallRuleInput = Omit<FirewallRule, 'id' | 'hits' | 'last_hit'> & { id?: string };

export function useSaveFirewallRule(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, FirewallRuleInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...rule }: FirewallRuleInput) => {
      // PUT when we have an existing rule id (UUID string), else POST a new one.
      const res = id
        ? await serviceApiV3.put(`/cdn/domains/${domain}/firewall-rules/${id}`, rule)
        : await serviceApiV3.post(`/cdn/domains/${domain}/firewall-rules`, rule);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ['cdn', 'domains', domain, 'firewall-rules'] });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteFirewallRule(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await serviceApiV3.delete(`/cdn/domains/${domain}/firewall-rules/${id}`);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ['cdn', 'domains', domain, 'firewall-rules'] });
      options?.onSuccess?.(...args);
    },
  });
}

export function useToggleFirewall(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, boolean>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (enabled: boolean) => {
      await serviceApiV3.put(`/cdn/domains/${domain}/firewall-rules/enable`, { enabled });
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ['cdn', 'domains', domain, 'firewall-rules'] });
      options?.onSuccess?.(...args);
    },
  });
}

export interface FirewallTestRequest {
  method: string;
  path: string;
  query: string;
  user_agent: string;
  referer: string;
  content_type: string;
  client_ip: string;
  is_ssl: boolean;
  country_code: string;
  asn: number;
  threat_score: number;
}
export interface FirewallTestResponse {
  matched: boolean;
  rule_id: string | null;
  rule_name: string | null;
  action: string | null;
}

export function useTestFirewall(
  domain: string,
  options?: UseMutationOptions<FirewallTestResponse, unknown, FirewallTestRequest>,
) {
  return useMutation({
    mutationFn: async (req: FirewallTestRequest): Promise<FirewallTestResponse> => {
      const res = await serviceApiV3.post<Envelope<unknown>>(`/cdn/domains/${domain}/firewall-rules/test`, req);
      return firstVendor<FirewallTestResponse>(res.data?.data, {
        matched: false,
        rule_id: null,
        rule_name: null,
        action: null,
      });
    },
    ...options,
  });
}
